import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);

  // Rotas que não têm hero escuro atrás da navbar — forçar fundo branco sempre
  const forceWhiteNav =
    location.pathname.startsWith('/blog/') ||
    location.pathname.startsWith('/tutoriais/') ||
    location.pathname === '/mapa-do-site' ||
    location.pathname === '/busca' ||
    location.pathname === '/politica-de-privacidade' ||
    location.pathname === '/politica-de-cookies' ||
    location.pathname === '/termos-de-uso' ||
    location.pathname === '/sla';
  const effectiveScrolled = scrolled || forceWhiteNav;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  const navItems = useMemo(() => [
    { key: 'home', label: t('nav_home'), path: '/' },
    {
      key: 'trader',
      label: t('nav_trader'),
      path: '/traders',
      submenu: [
        { key: 'all', label: t('nav_trader_all'), icon: 'ri-layout-grid-line', path: '/traders' },
        { key: 'br', label: t('nav_trader_br'), icon: 'code:BR', path: '/vps-trader-br' },
        { key: 'uk', label: t('nav_trader_uk'), icon: 'code:UK', path: '/vps-trader-uk' },
        { key: 'us', label: t('nav_trader_us'), icon: 'code:US', path: '/vps-trader-us' },
      ],
    },
    {
      key: 'personal',
      label: t('nav_personal'),
      path: '/vps-pessoal',
      submenu: [
        { key: 'personal_all', label: t('nav_personal_all'), icon: 'ri-user-line', path: '/vps-pessoal' },
      ],
    },
    {
      key: 'dev',
      label: t('nav_dev'),
      path: '/vps-dev',
      submenu: [
        { key: 'dev_all', label: t('nav_dev_all'), icon: 'ri-code-s-slash-line', path: '/vps-dev' },
      ],
    },
    {
      key: 'corporate',
      label: t('nav_corporate'),
      path: '/vps-empresa',
      submenu: [
        { key: 'enterprise', label: t('nav_enterprise'), icon: 'ri-building-2-line', path: '/vps-empresa' },
      ],
    },
    {
      key: 'company',
      label: t('nav_company'),
      path: '/sobre',
      submenu: [
        { key: 'about', label: t('nav_about'), icon: 'ri-medal-line', path: '/sobre' },
        { key: 'datacenter', label: t('nav_datacenter'), icon: 'ri-server-line', path: '/data-center' },
        { key: 'contact', label: t('nav_contact'), icon: 'ri-phone-line', path: '/contato' },
        { key: 'tutoriais', label: 'Tutoriais', icon: 'ri-book-open-line', path: '/tutoriais' },
        { key: 'blog', label: 'Blog', icon: 'ri-article-line', path: '/blog' },
      ],
    },
  ], [t]);

  const renderSubIcon = (icon: string, muted = false) => {
    if (icon.startsWith('code:')) {
      const code = icon.replace('code:', '');
      return (
        <span className={`text-[10px] font-bold rounded px-1 py-0.5 leading-none ${muted ? 'bg-gray-100 text-gray-500' : 'bg-gray-100 text-gray-600'}`}>
          {code}
        </span>
      );
    }
    return (
      <span className="w-5 h-5 flex items-center justify-center">
        <i className={`${icon} text-base ${muted ? 'text-gray-400' : 'text-gray-500'}`} />
      </span>
    );
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setMobileOpen(false);
  }, [location.pathname]);

  const scrollToContact = () => {
    setMobileOpen(false);
    setOpenDropdown(null);
    navigate('/contato');
  };

  const handleNavClick = (item: (typeof navItems)[0]) => {
    if (item.submenu) {
      setOpenDropdown(openDropdown === item.key ? null : item.key);
    } else if (item.path) {
      setOpenDropdown(null);
      navigate(item.path);
    }
  };

  const handleSubClick = (sub: { key: string; label: string; flag?: string; path: string }) => {
    setOpenDropdown(null);
    setMobileOpen(false);
    navigate(sub.path);
  };

  const isActive = (item: (typeof navItems)[0]) => {
    if (item.path === '/') return location.pathname === '/';
    if (item.key === 'trader') {
      return location.pathname.startsWith('/traders') || location.pathname.startsWith('/vps-trader');
    }
    if (item.key === 'personal') {
      return location.pathname === '/vps-pessoal';
    }
    if (item.key === 'dev') {
      return location.pathname === '/vps-dev' || location.pathname === '/desenvolvedores';
    }
    if (item.key === 'corporate') {
      return location.pathname === '/vps-empresa' || location.pathname === '/corporativo';
    }
    if (item.key === 'company') {
      return location.pathname === '/sobre' || location.pathname === '/contato' || location.pathname === '/data-center' || location.pathname.startsWith('/tutoriais') || location.pathname.startsWith('/blog');
    }
    return location.pathname.startsWith(item.path || '');
  };

  return (
    <header
      ref={navRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        effectiveScrolled ? 'bg-white border-b border-gray-200' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => navigate('/')} className="flex items-center cursor-pointer shrink-0">
          <img
            src="https://musthost.com.br/img/logo.png"
            alt="musthost"
            width={137}
            height={48}
            fetchPriority="high"
            decoding="sync"
            style={{ width: 137, height: 48 }}
            className={`object-contain transition-all ${!effectiveScrolled ? 'brightness-0 invert' : ''}`}
          />
        </button>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => {
            const active = isActive(item);
            return (
              <div key={item.key} className="relative">
                {item.submenu ? (
                  <button
                    onClick={() => handleNavClick(item)}
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                      effectiveScrolled
                        ? active
                          ? 'text-blue-700 bg-blue-50'
                          : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                        : active
                        ? 'text-white bg-white/15'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {item.label}
                    <i className={`ri-arrow-down-s-line text-sm transition-transform ${openDropdown === item.key ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <a
                    href={item.path || '/'}
                    onClick={(e) => {
                      e.preventDefault();
                      if (item.path) navigate(item.path);
                    }}
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                      effectiveScrolled
                        ? active
                          ? 'text-blue-700 bg-blue-50'
                          : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                        : active
                        ? 'text-white bg-white/15'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {item.label}
                  </a>
                )}

                {item.submenu && openDropdown === item.key && (
                  <div
                    className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-xl overflow-hidden min-w-[190px]"
                    style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
                  >
                    {item.submenu.map((sub) => (
                      <a
                        key={sub.key}
                        href={sub.path}
                        onClick={(e) => {
                          e.preventDefault();
                          handleSubClick(sub);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:text-blue-700 hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap text-left"
                      >
                        {sub.icon && renderSubIcon(sub.icon)}
                        <span>{sub.label}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Right actions */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://musthost.com.br/sac/"
            target="_blank"
            rel="noopener noreferrer"
            className={`px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${
              effectiveScrolled ? 'text-gray-600 hover:text-blue-700' : 'text-white/80 hover:text-white'
            }`}
          >
            {t('nav_client_area')}
          </a>
          <button
            onClick={scrollToContact}
            className="px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            {t('nav_hire')}
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${
            effectiveScrolled ? 'text-gray-700' : 'text-white'
          }`}
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 px-4 py-3 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.key}>
              <button
                onClick={() => {
                  if (item.submenu) {
                    setMobileExpanded(mobileExpanded === item.key ? null : item.key);
                  } else if (item.path) {
                    setMobileOpen(false);
                    navigate(item.path);
                  }
                }}
                className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-blue-700 hover:bg-blue-50 rounded-lg cursor-pointer transition-all whitespace-nowrap"
              >
                {item.label}
                {item.submenu && (
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${mobileExpanded === item.key ? 'rotate-180' : ''}`} />
                )}
              </button>

              {item.submenu && mobileExpanded === item.key && (
                <div className="ml-4 mt-1 flex flex-col gap-1">
                  {item.submenu.map((sub) => (
                    <button
                      key={sub.key}
                      onClick={() => handleSubClick(sub)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg cursor-pointer transition-all whitespace-nowrap text-left"
                    >
                      {sub.icon && renderSubIcon(sub.icon, true)}
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          <a
            href="https://musthost.com.br/sac/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
            className="mt-1 px-4 py-3 border border-blue-200 bg-blue-50 text-blue-700 text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap text-center block"
          >
            <i className="ri-user-line mr-1.5" />
            {t('nav_client_area')}
          </a>
          <button
            onClick={scrollToContact}
            className="mt-1 px-4 py-3 bg-blue-700 text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap text-center"
          >
            {t('nav_hire')}
          </button>
        </div>
      )}
    </header>
  );
}
